import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fetchProducts } from "../lib/cache_api";
import Papa from "papaparse";
import { useAuth } from "../lib/auth";
import { useBilling, parseTierLimitError } from "../lib/billing";
import CategoryTree from "../components/CategoryTree";
import QuickEditSheet from "../components/QuickEditSheet";
import { Plus, Minus, Search, Download, Upload, Trash2, Settings2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, fmtInt } from "../lib/format";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [tree, setTree] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const { user } = useAuth();
  const { openUpgradeModal } = useBilling();
  const isAdmin = user?.role === "admin";
  const [quickEdit, setQuickEdit] = useState(null);

  const load = async () => {
    try {
      const [{ data: prods }, c] = await Promise.all([fetchProducts(), api.get("/categories")]);
      setProducts(prods);
      setTree(c.data?.tree || []);
    } catch (_) {
      // /categories may fail offline — fall back: derive a flat tree from cached products
      const { data: prods } = await fetchProducts();
      setProducts(prods);
      const seen = new Map();
      for (const p of prods) for (const cat of (p.categories || [])) {
        seen.set(cat, (seen.get(cat) || 0) + 1);
      }
      const flat = Array.from(seen, ([name, count]) => ({ name, path: name, self_count: count, total: count, children: [] }));
      setTree(flat);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (activeCat) {
      list = list.filter((p) => (p.categories || []).some((c) => c === activeCat || c.startsWith(activeCat + "/")));
    }
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((p) =>
      p.name.toLowerCase().includes(s) ||
      (p.categories || []).join(" ").toLowerCase().includes(s) ||
      (p.variants || []).some((v) => v.sku.toLowerCase().includes(s) || v.barcode.includes(s))
    );
  }, [products, q, activeCat]);

  const totalStock = (p) => (p.variants || []).reduce((a, v) => a + Number(v.stock || 0), 0);
  const stockTone = (p) => {
    const s = totalStock(p);
    if (s === 0) return "!bg-[#D9381E] !text-white";
    if (s <= (p.low_stock_threshold || 5)) return "!bg-[#FFB100]";
    return "!bg-[#4A7C59] !text-white";
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  // Quick stock adjust for single-variant products. Optimistic update + revert on failure.
  const [adjustingId, setAdjustingId] = useState(null);
  const quickAdjust = async (product, delta) => {
    const v = (product.variants || [])[0];
    if (!v) return;
    if (v.stock + delta < 0) return; // no negative stock
    setAdjustingId(`${product.product_id}:${delta > 0 ? "+" : "-"}`);
    // Optimistic
    setProducts((prev) => prev.map((p) =>
      p.product_id === product.product_id
        ? { ...p, variants: p.variants.map((vv, i) => i === 0 ? { ...vv, stock: vv.stock + delta } : vv) }
        : p
    ));
    try {
      await api.post(`/products/${product.product_id}/variants/${v.variant_id}/adjust`, null, { params: { delta } });
    } catch (err) {
      // Revert on failure
      setProducts((prev) => prev.map((p) =>
        p.product_id === product.product_id
          ? { ...p, variants: p.variants.map((vv, i) => i === 0 ? { ...vv, stock: vv.stock - delta } : vv) }
          : p
      ));
      toast.error("Couldn't update stock");
    } finally { setAdjustingId(null); }
  };

  const exportCSV = async () => {
    const res = await api.get("/products/export/csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "inventory.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const triggerImport = () => fileRef.current?.click();
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (res) => {
        try {
          const { data } = await api.post("/products/import/csv", { rows: res.data });
          toast.success(`Imported ${data.created} products`);
          load();
        } catch (err) {
          const tierErr = parseTierLimitError(err);
          if (tierErr) openUpgradeModal(tierErr);
          else toast.error("Import failed");
        }
      }
    });
    e.target.value = "";
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Catalog</div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">Products</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <>
              <button onClick={exportCSV} className="brutal-btn brutal-btn-secondary" data-testid="export-csv-btn">
                <Download size={14}/> Export CSV
              </button>
              <button onClick={triggerImport} className="brutal-btn brutal-btn-secondary" data-testid="import-csv-btn">
                <Upload size={14}/> Import CSV
              </button>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleImport} className="hidden"/>
              <Link to="/products/new" className="brutal-btn" data-testid="new-product-btn">
                <Plus size={14}/> New Item
              </Link>
            </>
          )}
          {!isAdmin && (
            <span className="brutal-badge" data-testid="staff-readonly-badge">Staff · Read-only</span>
          )}
        </div>
      </div>

      <div className="brutal-card-sm flex items-center gap-2 px-3 py-2 bg-white">
        <Search size={16}/>
        <input
          className="flex-1 outline-none font-mono text-sm py-2"
          placeholder="Search name, SKU, barcode…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="products-search"
        />
      </div>

      {activeCat && (
        <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A] flex items-center gap-2">
          <span>Filter:</span>
          <span className="brutal-badge !bg-[#C2410C] !text-white">{activeCat}</span>
          <button onClick={()=>setActiveCat("")} className="font-mono text-xs underline">Clear</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <CategoryTree tree={tree} active={activeCat} onSelect={setActiveCat} totalAll={products.length}/>
        </aside>
        <div>

      {filtered.length === 0 ? (
        <div className="brutal-card p-10 text-center">
          <div className="font-heading text-2xl font-black uppercase">No products yet</div>
          <p className="font-mono text-sm text-[#4A4A4A] mt-2">{isAdmin ? "Add your first item to get started." : "Ask your admin to add products."}</p>
          {isAdmin && <Link to="/products/new" className="brutal-btn mt-4 inline-flex">+ New Item</Link>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="brutal-table" data-testid="products-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Categories</th>
                <th>Variants</th>
                <th>Stock</th>
                <th>Quick adjust</th>
                <th>Value</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                  <td>
                    {p.photo_base64 ? (
                      <img src={p.photo_base64} alt="" className="w-12 h-12 object-cover border-2 border-black"/>
                    ) : (
                      <div className="w-12 h-12 bg-[#EAE7DF] border-2 border-dashed border-black"/>
                    )}
                  </td>
                  <td>
                    <Link to={`/products/${p.product_id}`} className="font-heading font-bold hover:underline">
                      {p.name}
                    </Link>
                    <div className="font-mono text-[10px] text-[#4A4A4A] uppercase tracking-widest">{p.base_sku}</div>
                  </td>
                  <td className="font-mono text-xs">
                    {(p.categories || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(p.categories || []).map((c) => (
                          <span key={c} className="brutal-badge !py-0 !px-1 !text-[9px]">{c}</span>
                        ))}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="font-mono text-xs">{(p.variants || []).length}</td>
                  <td>
                    <span className={`brutal-badge ${stockTone(p)}`}>{fmtInt(totalStock(p))}</span>
                  </td>
                  <td>
                    {isAdmin && ((p.variants || []).length === 1 ? (
                      <div className="inline-flex items-center gap-1 flex-wrap" data-testid={`quick-adjust-${p.product_id}`}>
                        <button
                          onClick={() => quickAdjust(p, -1)}
                          disabled={(p.variants?.[0]?.stock ?? 0) <= 0 || adjustingId === `${p.product_id}:-`}
                          className="brutal-btn brutal-btn-secondary !p-1 !min-w-[32px] !min-h-[32px] disabled:opacity-30"
                          aria-label="Decrement stock"
                          data-testid={`quick-stock-minus-${p.product_id}`}
                        ><Minus size={12}/></button>
                        <button
                          onClick={() => quickAdjust(p, 1)}
                          disabled={adjustingId === `${p.product_id}:+`}
                          className="brutal-btn !p-1 !min-w-[32px] !min-h-[32px]"
                          aria-label="Increment stock"
                          data-testid={`quick-stock-plus-${p.product_id}`}
                        ><Plus size={12}/></button>
                        <button
                          onClick={() => setQuickEdit(p)}
                          className="brutal-btn brutal-btn-secondary !p-1 !min-w-[32px] !min-h-[32px] sm:hidden"
                          aria-label="Quick edit"
                          data-testid={`quick-edit-open-${p.product_id}`}
                        ><Pencil size={12}/></button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setQuickEdit(p)}
                          className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1"
                          data-testid={`quick-edit-open-${p.product_id}`}
                          aria-label="Quick edit"
                        >
                          <Pencil size={12}/>
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                        <Link
                          to={`/products/${p.product_id}`}
                          className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1"
                          data-testid={`manage-variants-${p.product_id}`}
                        >
                          <Settings2 size={12}/>
                          <span className="hidden sm:inline">Manage</span>
                        </Link>
                      </div>
                    ))}
                  </td>
                  <td className="font-mono text-xs">
                    {fmtMoney((p.variants || []).reduce((a,v) => a + Number(v.stock||0)*Number(v.price||0), 0))}
                  </td>
                  <td>
                    {isAdmin && (
                      <button
                        className="brutal-btn brutal-btn-danger !py-1 !px-2 text-xs"
                        onClick={() => doDelete(p.product_id)}
                        data-testid={`delete-product-${p.product_id}`}
                      >
                        <Trash2 size={12}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </div>
      </div>
      {quickEdit && (
        <QuickEditSheet
          product={quickEdit}
          onClose={() => setQuickEdit(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
